import axios, { AxiosHeaders } from "axios";
import type { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from "axios";

import { API, CSRF_COOKIE_NAME, CSRF_HEADER_NAME, NameOfRoutes } from "@shared/constants";
import type { HttpMethod } from "@shared/constants";
import authAPI from "@containers/Auth/api/api";

import { tokenHandler } from "./tokenHandler";
import { getCookie } from "./cookies";

interface RetriableConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// The methods take different argument lists (`get(url, config)` vs `post(url, data, config)`),
// so they are called through one loose signature rather than indexed straight off the instance.
type RequestMethod = (url: string, ...params: unknown[]) => Promise<unknown>;

axios.defaults.headers.post["Content-Type"] = "application/json";

// The CSRF header is set by hand in the request interceptor. Axios' own XSRF handling is switched
// off (empty cookie name), otherwise it also sends `X-XSRF-TOKEN` whenever the cookie is named
// exactly `XSRF-TOKEN`, as in production.
const axiosInstance: AxiosInstance = axios.create({ withCredentials: true, xsrfCookieName: "" });

let refreshPromise: Promise<void> | null = null;

/**
 * Exchanges the refresh token cookie for a new access token. Callers share a single in-flight
 * request, so a burst of rejected requests rotates the refresh token only once.
 *
 * @return {Promise<void>} resolves once the new access token is stored
 */
export const refreshAccessToken = (): Promise<void> => {
  if (!refreshPromise) {
    refreshPromise = authAPI
      .refreshToken()
      .then((response) => tokenHandler.set((response as { token: string }).token))
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise as Promise<void>;
};

/**
 * Drops the session and sends the user back to the login page: once the refresh token is gone
 * the app cannot restore the session on its own.
 */
const endSession = () => {
  tokenHandler.clear();

  if (window.location.pathname.startsWith(NameOfRoutes.AUTH)) return;

  localStorage.clear();
  window.location.assign(NameOfRoutes.AUTH_LOGIN);
};

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenHandler.get();
  const csrfToken = getCookie(CSRF_COOKIE_NAME);

  if (!token && !csrfToken) {
    return config;
  }

  return {
    ...config,
    headers: new AxiosHeaders({
      ...config.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
    }),
  };
});

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const response = res.data || res;
    if (response.error) {
      return Promise.reject(response.error);
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = (error.config || {}) as RetriableConfig;
    const { data } = error.response || {};

    const isAuthEndpoint = config.url === API.AUTH.REFRESH_TOKEN || config.url === API.AUTH.LOGOUT;
    // 401 means the access token expired. 403 is either a CSRF cookie the API no longer accepts
    // or a token it no longer recognises — a refresh reissues both, so it answers all three.
    const isRejected = error.response?.status === 401 || error.response?.status === 403;

    const shouldAttemptRefresh = isRejected && !isAuthEndpoint && !config._retry && !!config.headers?.["Authorization"];

    if (shouldAttemptRefresh) {
      try {
        await refreshAccessToken();
      } catch {
        // The whole session is gone, not just the access token.
        await authAPI.logout().catch(() => null);
        endSession();

        return Promise.reject(data || error);
      }

      return axiosInstance.request({ ...config, _retry: true } as RetriableConfig);
    }

    if (data) {
      return Promise.reject(data);
    }

    return Promise.reject(error);
  },
);

/**
 * Axios wrapper
 *
 * @param  {HttpMethod} method Method of the request
 * @param  {string} url url of the request
 *
 * @return {function} wrapped axios function that receives params
 */
export default (method: HttpMethod, url: string) =>
  (...params: unknown[]) =>
    (axiosInstance[method] as RequestMethod)(url, ...params);
